import { InfoCircleOutlined } from '@ant-design/icons';
import { Flex, Form, Input, Modal, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getCronFieldDefinitions,
  validateCronExpression,
} from '../../../../utils';

const { Text, Paragraph } = Typography;

const CRON_FIELD_KEYS = [
  'minute',
  'hour',
  'dayOfMonth',
  'month',
  'dayOfWeek',
] as const;

export interface CronSettingInputProps {
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

export function CronSettingInput({
  value,
  disabled,
  onChange,
}: CronSettingInputProps) {
  const { t } = useTranslation('settings');
  const [helpOpen, setHelpOpen] = useState(false);
  const isValid = validateCronExpression(value);

  return (
    <Flex vertical gap={8}>
      <Flex align="center" gap={8}>
        <Form.Item
          validateStatus={isValid ? undefined : 'error'}
          help={isValid ? undefined : t('cron.validationError')}
          className="platform-setting-cron-field"
        >
          <Input
            value={value}
            status={isValid ? undefined : 'error'}
            disabled={disabled}
            placeholder={t('cron.placeholder')}
            onChange={(event) => onChange(event.target.value)}
          />
        </Form.Item>
        <Tooltip title={t('cron.formatHelpTooltip')}>
          <button
            type="button"
            className="platform-setting-cron-info"
            aria-label={t('cron.formatHelpAria')}
            onClick={() => setHelpOpen(true)}
          >
            <InfoCircleOutlined aria-hidden />
          </button>
        </Tooltip>
      </Flex>
      <div className="platform-setting-cron-hints">
        {getCronFieldDefinitions().map((field) => (
          <Tag key={field.key}>
            <Text strong>{t(`cron.fields.${field.key}`)}</Text>
            {` ${field.min}-${field.max}`}
          </Tag>
        ))}
      </div>
      <Modal
        open={helpOpen}
        title={t('cron.formatHelpTitle')}
        footer={null}
        centered
        destroyOnHidden
        width={720}
        className="platform-setting-cron-help-modal"
        onCancel={() => setHelpOpen(false)}
      >
        <Flex vertical gap={20} className="platform-setting-cron-help">
          <Paragraph
            type="secondary"
            className="platform-setting-cron-help-intro"
          >
            {t('cron.formatHelpIntro')}
          </Paragraph>

          <div className="platform-setting-cron-help-example" aria-hidden>
            {CRON_FIELD_KEYS.map((fieldKey) => (
              <div key={fieldKey} className="platform-setting-cron-help-token">
                <span className="platform-setting-cron-help-star">*</span>
                <span className="platform-setting-cron-help-token-label">
                  {t(`cron.fields.${fieldKey}`)}
                </span>
              </div>
            ))}
          </div>

          <ol className="platform-setting-cron-help-list">
            {CRON_FIELD_KEYS.map((fieldKey, index) => (
              <li key={fieldKey}>
                <Text strong>
                  {index + 1}. {t(`cron.fields.${fieldKey}`)}
                </Text>
                <Text type="secondary">{t(`cron.fieldHelp.${fieldKey}`)}</Text>
              </li>
            ))}
          </ol>
        </Flex>
      </Modal>
    </Flex>
  );
}
